import React, { useState, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  CircularProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { pl } from 'date-fns/locale';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { AuthContext } from '../../contexts/AuthContext';

// Typy kontaktów
const CONTACT_TYPES = {
  phone: 'Rozmowa telefoniczna',
  email: 'Email',
  meeting: 'Spotkanie',
  message: 'Wiadomość',
  other: 'Inny'
};

const AddContactModal = ({ open, handleClose, clientId, clientName, onContactAdded }) => {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState({
    type: 'phone',
    date: new Date(),
    description: '',
    notes: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContact(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (newDate) => {
    setContact(prev => ({
      ...prev,
      date: newDate
    }));
  };

  const handleAddContact = async () => {
    try {
      setLoading(true);

      // Przygotuj dane kontaktu
      const contactData = {
        ...contact,
        clientId,
        employeeId: currentUser.uid,
        employeeName: currentUser.displayName || currentUser.email,
        created_at: new Date(),
        created_by: {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || ''
        }
      };

      // Dodaj dokument kontaktu
      const docRef = await addDoc(collection(db, 'contacts'), contactData);

      // Zaktualizuj datę ostatniego kontaktu klienta
      await updateDoc(doc(db, 'clients', clientId), {
        lastContactDate: contactData.date,
        updated_at: new Date(),
        updated_by: {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || ''
        }
      });

      // Wywołaj callback
      if (onContactAdded) {
        onContactAdded({
          id: docRef.id,
          ...contactData
        });
      }

      // Zamknij modal
      handleClose();
    } catch (error) {
      console.error('Błąd podczas dodawania kontaktu:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Dodaj kontakt z klientem</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Klient: {clientName}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Typ kontaktu</InputLabel>
              <Select
                name="type"
                value={contact.type}
                onChange={handleInputChange}
                label="Typ kontaktu"
              >
                {Object.entries(CONTACT_TYPES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
              <DateTimePicker
                label="Data i godzina"
                value={contact.date}
                onChange={handleDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Opis kontaktu"
              name="description"
              value={contact.description}
              onChange={handleInputChange}
              required
              placeholder="Krótki opis kontaktu..."
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notatki"
              name="notes"
              value={contact.notes}
              onChange={handleInputChange}
              multiline
              rows={4}
              placeholder="Dodatkowe informacje..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Anuluj</Button>
        <Button 
          onClick={handleAddContact} 
          variant="contained" 
          disabled={loading || !contact.description}
        >
          {loading ? <CircularProgress size={24} /> : 'Dodaj kontakt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddContactModal;
